import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { Send, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { businessInfo, images } from "../mock";
import { useToast } from "../hooks/use-toast";

// Topic-aware prefill messages — keyed by landing page slug.
// Triggered when user lands on / with ?topic=<slug>#contact.
const topicPrefills = {
  "denied-insurance-claim-florida":
    "Hi — my Florida home insurance claim was DENIED. I'd like a free review to see if it can be reopened.\n\nDetails about my denial: ",
  "underpaid-insurance-claim-florida":
    "Hi — my Florida home insurance claim was UNDERPAID. The carrier's offer doesn't cover the actual repair cost. I'd like a free review.\n\nDetails about my claim: ",
  "roof-claim-denial-florida":
    "Hi — my Florida ROOF CLAIM was denied. I'd like a free review to see if it can be reopened.\n\nDate of loss / type of damage: ",
};

const initialForm = {
  firstName: "",
  lastName: "",
  address: "",
  phone: "",
  email: "",
  message: "",
  // Honeypot field — must remain empty. Real users won't see/fill it.
  website: "",
};

const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

// Minimum seconds a real user takes to read + fill the form. Faster = likely bot.
const MIN_FILL_SECONDS = 3;

// Read ?topic= from URL once at module load — used to lazy-init form state.
const getInitialPrefill = () => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const topic = params.get("topic");
  return topic && topicPrefills[topic] ? topicPrefills[topic] : "";
};

const Contact = () => {
  const [form, setForm] = useState(() => ({
    ...initialForm,
    message: getInitialPrefill(),
  }));
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const [formMountedAt] = useState(() => Date.now());

  // Smooth-scroll into view when arriving with a topic prefill
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("topic") && topicPrefills[params.get("topic")]) {
      const el = document.getElementById("contact");
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          100
        );
      }
    }
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();

    // --- Spam protection: honeypot + time-trap (silent reject) ---
    const elapsedSeconds = (Date.now() - formMountedAt) / 1000;
    if (form.website || elapsedSeconds < MIN_FILL_SECONDS) {
      // Pretend success so bots don't retry; never hit EmailJS.
      setSubmitted(true);
      setForm(initialForm);
      return;
    }

    if (!form.firstName || !form.address || !form.phone || !form.email) {
      toast({
        title: "Missing required fields",
        description: "Please fill out all required fields marked with *.",
      });
      return;
    }

    setSending(true);

    // EmailJS template variables
    const templateParams = {
      from_name: `${form.firstName} ${form.lastName}`.trim(),
      first_name: form.firstName,
      last_name: form.lastName,
      from_email: form.email,
      reply_to: form.email,
      phone: form.phone,
      address: form.address,
      message: form.message || "(no message provided)",
      submitted_at: new Date().toLocaleString("en-US", {
        timeZone: "America/New_York",
      }),
      to_email: businessInfo.emailJoe,
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      // Also save locally as a backup
      const submissions = JSON.parse(
        localStorage.getItem("cb_submissions") || "[]"
      );
      submissions.push({ ...form, ts: new Date().toISOString() });
      localStorage.setItem("cb_submissions", JSON.stringify(submissions));

      setSubmitted(true);
      setForm(initialForm);
      toast({
        title: "Free claim review requested",
        description: "Thanks! Joe will contact you within 1 business day.",
      });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast({
        title: "Submission failed",
        description:
          "Something went wrong sending your request. Please call " +
          businessInfo.phone +
          " or try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0A0A0A] py-20 md:py-28 relative">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E11D26]/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <div
            className="text-[#E11D26] text-5xl sm:text-6xl font-black tracking-wider"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            BEAST
          </div>
          <h2
            className="mt-4 text-white text-3xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Ready To Stop Leaving <br />
            <span className="text-[#E11D26]">Money On The Table?</span>
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto">
            Let Claim Beast fight for your settlement. Fill out the form below
            or call us anytime — free review, zero upfront cost.
          </p>
          <a
            href={`tel:${businessInfo.phoneRaw}`}
            className="mt-6 inline-flex items-center gap-2 text-white hover:text-[#E11D26] transition-colors text-sm font-bold tracking-wider uppercase"
          >
            <Phone className="h-4 w-4" /> Or Call Us Now: {businessInfo.phone}
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="relative h-full min-h-[400px] overflow-hidden border border-white/10">
              <img
                src={images.hurricane}
                alt="Florida home damaged by hurricane storm"
                className="w-full h-full object-cover absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#E11D26]/40" />
              <div className="relative p-8 lg:p-10 h-full flex flex-col justify-end text-white">
                <h3
                  className="text-3xl sm:text-4xl font-black uppercase leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  Get Your Free Claim Review
                </h3>
                <p className="mt-3 text-white/85 text-sm">
                  Our Tampa Bay public adjusters will inspect, document, and
                  fight for your full payout.
                </p>
                <div className="mt-6 space-y-3 text-sm">
                  <a
                    href={`tel:${businessInfo.phoneRaw}`}
                    className="flex items-center gap-3 hover:text-[#ffb3b6]"
                  >
                    <Phone className="h-4 w-4 text-[#E11D26]" />
                    {businessInfo.phone}
                  </a>
                  <a
                    href={`mailto:${businessInfo.emailJoe}`}
                    className="flex items-center gap-3 hover:text-[#ffb3b6]"
                  >
                    <Mail className="h-4 w-4 text-[#E11D26]" />
                    {businessInfo.emailJoe}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-[#0F0F0F] border border-white/10 p-7 sm:p-9"
            >
              {submitted ? (
                <div className="flex flex-col items-center text-center py-10">
                  <CheckCircle2 className="h-14 w-14 text-[#E11D26]" />
                  <h3
                    className="mt-5 text-white text-3xl font-black uppercase"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    Request Received
                  </h3>
                  <p className="mt-3 text-white/70 max-w-md">
                    Thanks — Joe will contact you within 1 business day. For
                    urgent damage, call {businessInfo.phone}.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-[#E11D26] text-sm font-bold tracking-widest uppercase hover:text-white"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <>
                  {/* Honeypot field — hidden from real users, catches bots */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="cb-website">
                      Website (leave this field empty)
                    </label>
                    <input
                      id="cb-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormInput
                      label="First name"
                      name="firstName"
                      required
                      value={form.firstName}
                      onChange={onChange}
                      disabled={sending}
                    />
                    <FormInput
                      label="Last name"
                      name="lastName"
                      value={form.lastName}
                      onChange={onChange}
                      disabled={sending}
                    />
                  </div>
                  <FormInput
                    label="Property address"
                    name="address"
                    required
                    value={form.address}
                    onChange={onChange}
                    disabled={sending}
                  />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormInput
                      label="Phone"
                      name="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={onChange}
                      disabled={sending}
                    />
                    <FormInput
                      label="Email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      disabled={sending}
                    />
                  </div>
                  <div className="mt-5">
                    <label className="text-white/80 text-xs font-bold tracking-[0.2em] uppercase">
                      Tell us about your damage (optional)
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={onChange}
                      disabled={sending}
                      className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#E11D26] outline-none text-white py-2 text-base resize-none disabled:opacity-50"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] disabled:bg-[#7a1015] disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider px-8 py-4 transition-colors group"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Sending…</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm">
                          Submit Free Review Request
                        </span>
                        <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-white/45 text-xs">
                    By submitting you agree to be contacted by Claim Beast
                    Public Adjusters about your claim. No spam.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormInput = ({ label, name, required, ...rest }) => (
  <div>
    <label className="text-white/80 text-xs font-bold tracking-[0.2em] uppercase">
      {label}
      {required && <span className="text-[#E11D26] ml-1">*</span>}
    </label>
    <input
      name={name}
      required={required}
      {...rest}
      className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#E11D26] outline-none text-white py-2 text-base disabled:opacity-50"
    />
  </div>
);

export default Contact;
